import { Box, HStack, VStack } from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import { curveLinear, curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { LinearGradient } from '@visx/gradient';
import { GridColumns, GridRows } from '@visx/grid';
import { withParentSize } from '@visx/responsive';
import {
  WithParentSizeProps,
  WithParentSizeProvidedProps,
} from '@visx/responsive/lib/enhancers/withParentSize';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Bar, LinePath } from '@visx/shape';
import { defaultStyles, TooltipWithBounds, withTooltip } from '@visx/tooltip';
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip';
import { bisector, extent, max, min } from '@visx/vendor/d3-array';
import { CSSProperties, MouseEvent, ReactNode, TouchEvent, useCallback, useMemo } from 'react';

import { useChartStructureColors } from '../status-tag/chart-colors';
import { ChartAccessor, ChartCurveType, ChartScaleType } from './area';

export interface LineSeries<T extends object> {
  id: string;
  name: string;
  data: T[];
  color: string;
}

export interface LineChartProps<T extends object> {
  series: LineSeries<T>[];
  xAccessor: ChartAccessor<T>;
  yAccessor: ChartAccessor<T>;
  curveType?: ChartCurveType;
  tooltip?: (data: {
    xValue: number | Date;
    items: { series: LineSeries<T>; datum: T }[];
  }) => ReactNode;
  noTooltip?: boolean;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  tooltipStyles?: CSSProperties;
  backgroundColor1?: string;
  backgroundColor2?: string;
  axisStyles?: CSSProperties;
  noAxis?: boolean;
  borderRadius?: number;
  verticalGridColor?: string;
  horizontalGridColor?: string;
}

export const LineChart = withParentSize(withTooltip(LineChartComponent)) as unknown as <
  T extends object,
>(
  props: LineChartProps<T>,
) => JSX.Element;

function LineChartComponent<T extends object>({
  series,
  xAccessor,
  yAccessor,
  curveType,
  tooltip,
  noTooltip,
  parentWidth = 0,
  parentHeight = 0,
  margin = {},
  showTooltip,
  hideTooltip,
  tooltipStyles,
  tooltipData,
  tooltipTop = 0,
  tooltipLeft = 0,
  borderRadius = 14,
  backgroundColor1 = 'transparent',
  backgroundColor2 = 'transparent',
  axisStyles,
  noAxis,
  verticalGridColor = 'rgba(203, 213, 224, 0)',
  horizontalGridColor = 'rgba(203, 213, 224, 0.40)',
}: LineChartProps<T> &
  WithTooltipProvidedProps<{
    xValue: number | Date;
    items: { series: LineSeries<T>; datum: T }[];
  }> &
  WithParentSizeProvidedProps &
  WithParentSizeProps) {
  const {
    top: marginTop = 0,
    right: marginRight = 0,
    bottom: marginBottom = 0,
    left: marginLeft = 0,
  } = margin;
  const axisHeight = axisStyles?.height ? parseInt(String(axisStyles.height), 10) : 18;
  const width = Math.max(0, parentWidth ?? 0);
  const height = Math.max(0, (parentHeight ?? 0) - axisHeight);
  const { axisLabel: axisColor } = useChartStructureColors();

  const fullTooltipStyles = useMemo(
    () => ({
      ...defaultStyles,
      background: 'rgba(0, 0, 0, 0.64)',
      borderRadius: '8px',
      color: 'white',
      fontFamily: 'Inter, arial, sans-serif',
      fontSize: '10px',
      fontWeight: 400,
      ...tooltipStyles,
    }),
    [tooltipStyles],
  );

  const curve = useMemo(
    () => (curveType === ChartCurveType.Smooth ? curveMonotoneX : curveLinear),
    [curveType],
  );

  const innerWidth = Math.max(0, width - marginLeft - marginRight);
  const innerHeight = Math.max(0, height - marginTop - marginBottom);

  const sanitizedSeries = useMemo(
    () =>
      series
        .map((item) => ({
          ...item,
          data: item.data.filter((datum) => {
            const xValue = xAccessor.accessor(datum);
            const yValue = yAccessor.accessor(datum);

            return Number.isFinite(xValue.valueOf()) && Number.isFinite(yValue.valueOf());
          }),
        }))
        .filter((item) => item.data.length > 0),
    [series, xAccessor, yAccessor],
  );
  const canRenderChart =
    innerWidth > 0 && innerHeight > 0 && sanitizedSeries.some((item) => item.data.length > 0);

  const allData = useMemo(() => sanitizedSeries.flatMap((s) => s.data), [sanitizedSeries]);

  const bisectX = useMemo(
    () => bisector((d: T) => xAccessor.accessor(d).valueOf()).left,
    [xAccessor],
  );

  const createScaleFor = useCallback(
    (accessor: ChartAccessor<T>, range: number[], linearLimit = 0) => {
      if (allData.length === 0) {
        return accessor.type === ChartScaleType.Time
          ? scaleTime({ domain: [new Date(0), new Date(1)], range })
          : scaleLinear({ domain: [0, 1], range, round: true });
      }
      return accessor.type === ChartScaleType.Time
        ? scaleTime({
            domain: accessor.domain ?? (extent(allData, accessor.accessor) as [Date, Date]),
            range,
          })
        : scaleLinear({
            domain: accessor.domain ?? [0, (max(allData, accessor.accessor) || 0) + linearLimit],
            range,
            round: true,
          });
    },
    [allData],
  );

  const xScale = useMemo(
    () => createScaleFor(xAccessor, [marginLeft, innerWidth + marginLeft]),
    [createScaleFor, innerWidth, marginLeft, xAccessor],
  );
  const yScale = useMemo(
    () => createScaleFor(yAccessor, [innerHeight + marginTop, marginTop], innerHeight * 0.1),
    [createScaleFor, innerHeight, marginTop, yAccessor],
  );

  const handleTooltip = useCallback(
    (event: TouchEvent<SVGRectElement> | MouseEvent<SVGRectElement>) => {
      if (!canRenderChart) {
        return;
      }

      const { x = 0 } = localPoint(event) ?? {};
      const x0 = xScale.invert(x);

      // Find the closest point for each series
      const items: { series: LineSeries<T>; datum: T }[] = [];
      let minDiff = Infinity;
      let bestXValue: number | Date | null = null;

      sanitizedSeries.forEach((s) => {
        if (s.data.length === 0) return;
        const index = bisectX(s.data, x0, 1);
        const d0 = s.data[index - 1];
        const d1 = s.data[index];
        let d = d1;
        if (d0 && d1 && xAccessor.accessor(d1)) {
          d =
            x0.valueOf() - xAccessor.accessor(d0).valueOf() >
            xAccessor.accessor(d1).valueOf() - x0.valueOf()
              ? d1
              : d0;
        } else if (d0) {
          d = d0;
        }

        if (d) {
          const diff = Math.abs(xAccessor.accessor(d).valueOf() - x0.valueOf());
          items.push({ series: s, datum: d });
          if (diff < minDiff) {
            minDiff = diff;
            bestXValue = xAccessor.accessor(d);
          }
        }
      });

      if (items.length === 0 || bestXValue === null) {
        return;
      }

      const [firstItem, ...remainingItems] = items;

      if (!firstItem) {
        return;
      }

      // Snap tooltip to the closest x-value across all series
      const tooltipLeft = xScale(bestXValue);
      // For y-position, find the highest Y at this X across all series
      const maxDatum = remainingItems.reduce((prev, curr) => {
        const prevValue = yAccessor.accessor(prev.datum).valueOf();
        const currValue = yAccessor.accessor(curr.datum).valueOf();

        return prevValue > currValue ? prev : curr;
      }, firstItem);
      const tooltipTop = yScale(yAccessor.accessor(maxDatum.datum));

      showTooltip({
        tooltipData: { xValue: bestXValue, items },
        tooltipLeft,
        tooltipTop,
      });
    },
    [canRenderChart, xScale, bisectX, sanitizedSeries, xAccessor, showTooltip, yScale, yAccessor],
  );

  const getTooltip = useMemo(
    () =>
      tooltip ??
      ((data: { xValue: number | Date; items: { series: LineSeries<T>; datum: T }[] }) => (
        <VStack alignItems="start" spacing={2} minW="120px">
          <Text size="inherit" weight="bold">
            {xAccessor.tooltip?.(data.xValue as number & Date) ?? String(data.xValue)}
          </Text>
          <VStack w="full" spacing={1} alignItems="stretch">
            {data.items.map((item) => (
              <HStack key={item.series.id} justify="space-between" spacing={4}>
                <HStack spacing={2}>
                  <Box w="8px" h="8px" bg={item.series.color} borderRadius="full" />
                  <Box maxW="150px">
                    <Text size="sm" isTruncated>
                      {item.series.name}
                    </Text>
                  </Box>
                </HStack>
                <Text size="inherit" weight="medium">
                  {yAccessor.tooltip?.(yAccessor.accessor(item.datum) as number & Date) ??
                    String(yAccessor.accessor(item.datum))}
                </Text>
              </HStack>
            ))}
          </VStack>
        </VStack>
      )),
    [tooltip, xAccessor, yAccessor],
  );

  const axis = useMemo(() => {
    if (allData.length === 0) return null;
    const minX = min(allData, (d) => xAccessor.accessor(d).valueOf());
    const maxX = max(allData, (d) => xAccessor.accessor(d).valueOf());
    const firstObj = allData.find((d) => xAccessor.accessor(d).valueOf() === minX);
    const lastObj = allData.find((d) => xAccessor.accessor(d).valueOf() === maxX);

    return (
      <>
        {firstObj && (
          <div>
            {xAccessor.tooltip?.(xAccessor.accessor(firstObj) as number & Date) ??
              String(xAccessor.accessor(firstObj))}
          </div>
        )}
        {lastObj && minX !== maxX && (
          <div>
            {xAccessor.tooltip?.(xAccessor.accessor(lastObj) as number & Date) ??
              String(xAccessor.accessor(lastObj))}
          </div>
        )}
      </>
    );
  }, [allData, xAccessor]);

  const hasTooltip =
    Boolean(tooltipData) &&
    !noTooltip &&
    Number.isFinite(tooltipLeft) &&
    Number.isFinite(tooltipTop);
  const resolvedTooltipData = hasTooltip ? tooltipData : undefined;

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="url(#area-background-gradient)"
            rx={borderRadius}
          />
          <LinearGradient
            id="area-background-gradient"
            from={backgroundColor1}
            to={backgroundColor2}
          />
          {canRenderChart ? (
            <>
              <GridRows
                left={marginLeft}
                scale={yScale}
                width={innerWidth}
                strokeDasharray="1,3"
                stroke={horizontalGridColor}
                pointerEvents="none"
              />
              <GridColumns
                top={marginTop}
                scale={xScale}
                height={innerHeight}
                strokeDasharray="1,3"
                stroke={verticalGridColor}
                pointerEvents="none"
              />
              {sanitizedSeries.map((s) => (
                <LinePath<T>
                  key={s.id}
                  data={s.data}
                  curve={curve}
                  x={(d) => xScale(xAccessor.accessor(d))}
                  y={(d) => yScale(yAccessor.accessor(d))}
                  stroke={s.color}
                  strokeWidth={2}
                  shapeRendering="geometricPrecision"
                />
              ))}
              <Bar
                x={marginLeft}
                y={marginTop}
                width={innerWidth}
                height={innerHeight}
                fill="transparent"
                rx={14}
                onTouchStart={handleTooltip}
                onTouchMove={handleTooltip}
                onMouseMove={handleTooltip}
                onMouseLeave={() => hideTooltip()}
              />
            </>
          ) : null}
          {resolvedTooltipData ? (
            <g>
              <line
                x1={tooltipLeft}
                y1={marginTop}
                x2={tooltipLeft}
                y2={innerHeight + marginTop}
                stroke="rgba(203, 213, 224, 0.8)"
                strokeWidth={1}
                pointerEvents="none"
                strokeDasharray="4,4"
              />
              {resolvedTooltipData.items.map((item) => (
                <circle
                  key={item.series.id}
                  cx={xScale(xAccessor.accessor(item.datum))}
                  cy={yScale(yAccessor.accessor(item.datum))}
                  r={4}
                  fill={item.series.color}
                  pointerEvents="none"
                />
              ))}
            </g>
          ) : null}
        </svg>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: axisColor,
            fontFamily: 'Inter, arial, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            height: axisHeight,
            ...axisStyles,
          }}
        >
          {!noAxis && axis}
        </div>
      </div>
      {resolvedTooltipData ? (
        <TooltipWithBounds
          top={tooltipTop > innerHeight + marginTop - 50 ? tooltipTop - 50 : tooltipTop}
          left={tooltipLeft}
          style={fullTooltipStyles}
        >
          {getTooltip(resolvedTooltipData)}
        </TooltipWithBounds>
      ) : null}
    </div>
  );
}
